import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaCheckCircle, FaExclamationTriangle, FaSpinner, FaEdit, FaArrowLeft, FaRedo, FaPlus, FaSync } from "react-icons/fa";
import Toast from "../components/Toast";
import AddEntryModal, { ERROR_TABLE_MAP } from "../components/AddEntryModal";
import "./RenameBatchPage.css";
import GeminiWarnung from "../components/GeminiWarnung";
import { API_BASE as _API_BASE } from "../api/config";

const API_BASE = `${_API_BASE}/rename-me-ocr/api`;

/**
 * RenameBatchPage - Batch Review mit nativem PDF-Viewer
 * - Nutzt den Browser's nativen PDF-Viewer via <object>
 * - Text kann markiert und kopiert werden
 * - Zoom funktioniert nativ (Trackpad, Ctrl+Scroll)
 */
const RenameBatchPage = () => {
  const navigate = useNavigate();
  
  // === State ===
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [batchId, setBatchId] = useState(null);
  const [files, setFiles] = useState([]);
  const [errorFiles, setErrorFiles] = useState([]);
  const [successFiles, setSuccessFiles] = useState([]);
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  
  const [editingFileId, setEditingFileId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  
  // === Neue States für DB-Einträge Feature ===
  const [editedFileIds, setEditedFileIds] = useState(new Set());
  const [addEntryModalOpen, setAddEntryModalOpen] = useState(false);
  const [addEntryTarget, setAddEntryTarget] = useState(null); // { fileId, errorTag, prefillData }
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [reprocessing, setReprocessing] = useState(false);
  
  const inputRef = useRef(null);
  const editCancelledRef = useRef(false);

  const [collapsed, setCollapsed] = useState({ fehler: false, ok: true });
  const toggleSection = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  // === Load Files on Mount ===
  useEffect(() => {
    loadFiles();
    return () => {
      // Revoke blob URLs on cleanup
      if (pdfUrl && pdfUrl.startsWith('blob:')) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, []);

  const loadFiles = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API_BASE}/batch/dropbox/preview?limit=100&source=output`, {
        method: "POST",
      });
      
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      
      const data = await res.json();
      console.log("[REVIEW] Loaded:", data);
      
      setBatchId(data.batch_id);
      setFiles(data.files || []);
      
      const errors = (data.files || []).filter(f => f.has_error);
      const success = (data.files || []).filter(f => !f.has_error);
      
      setErrorFiles(errors);
      setSuccessFiles(success);
      
      // Erste Datei auswählen
      const firstFile = errors.length > 0 ? errors[0] : success[0];
      if (firstFile) {
        handleFileSelect(firstFile, batchId);
      }
      
    } catch (e) {
      console.error("[REVIEW] Error:", e);
      setError(`Fehler beim Laden: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  // === PDF Loading ===
  const handleFileSelect = async (file, bid = batchId) => {
    if (!file || !bid) return;
    if (file.id === selectedFile?.id) return;
    
    setSelectedFile(file);
    setEditingFileId(null);
    setPdfLoading(true);
    
    // Alte URL revoken
    if (pdfUrl && pdfUrl.startsWith('blob:')) {
      URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    
    try {
      // PDF als Blob fetchen
      const url = `${API_BASE}/batch/dropbox/file/${bid}/${file.id}`;
      console.log("[PDF] Fetching:", url);
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const blob = await response.blob();
      console.log("[PDF] Downloaded:", blob.size, "bytes");
      
      // Blob URL erstellen
      const blobUrl = URL.createObjectURL(blob);
      setPdfUrl(blobUrl);
      
    } catch (e) {
      console.error("[PDF] Load error:", e);
      setPdfUrl(null);
    } finally {
      setPdfLoading(false);
    }
  };

  // === Editing ===
  const startEdit = (e, fileId, currentName) => {
    e.stopPropagation();
    setEditingFileId(fileId);
    setEditValue(currentName.replace(/\.pdf$/i, ""));
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const stopEdit = () => {
    setEditingFileId(null);
    setEditValue("");
  };

  const handleNameChange = (newValue) => {
    setEditValue(newValue);
  };

  const saveFilename = async (fileId, newName, quelle = null) => {
    if (!batchId || !fileId || !newName.trim()) return;
    
    setSaving(true);
    try {
      const res = await fetch(
        `${API_BASE}/batch/dropbox/update-filename?batch_id=${batchId}&file_id=${fileId}&new_name=${encodeURIComponent(newName)}` + (quelle ? `&quelle=${encodeURIComponent(quelle)}` : ""),
        { method: "POST" }
      );
      
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || "Speichern fehlgeschlagen");
      }
      
      const data = await res.json();
      
      // Update files (Welle 79: Alternativen neu berechnen — der gewählte Name ist keine Alternative mehr)
      const _norm = (n) => (n || "").replace(/\.pdf$/i, "").replace(/\s+/g, " ").trim().toLowerCase();
      const updateFile = (f) => f.id === fileId 
        ? { ...f, proposed_name: data.new_name, has_error: data.has_error, route: data.route,
            alternativen: [
              ...(f.proposed_v1 && _norm(f.proposed_v1) !== _norm(data.new_name) ? [{ quelle: "v1", name: f.proposed_v1 }] : []),
              ...(f.proposed_gemini && _norm(f.proposed_gemini) !== _norm(data.new_name) ? [{ quelle: "gemini", name: f.proposed_gemini }] : []),
            ] }
        : f;
      
      const updatedFiles = files.map(updateFile);
      setFiles(updatedFiles);
      setErrorFiles(updatedFiles.filter(f => f.has_error));
      setSuccessFiles(updatedFiles.filter(f => !f.has_error));
      
      // Datei als bearbeitet markieren (Fehler UND OK)
      setEditedFileIds(prev => new Set([...prev, fileId]));

      // Update selected
      if (selectedFile?.id === fileId) {
        setSelectedFile(prev => ({ ...prev, proposed_name: data.new_name, has_error: data.has_error, route: data.route }));
      }
      
    } catch (e) {
      setError(`Speichern fehlgeschlagen: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  // === Apply All ===
  const handleApplyAll = async () => {
    const nonEdited = files.filter(f => !editedFileIds.has(f.id));
    if (!batchId || nonEdited.length === 0) return;
    
    setApplying(true);
    
    try {
      const excludeParam = editedFileIds.size > 0
        ? `&exclude_ids=${[...editedFileIds].join(",")}`
        : "";
      const res = await fetch(`${API_BASE}/batch/dropbox/apply-all?batch_id=${batchId}${excludeParam}`, {
        method: "POST",
      });
      
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      
      const data = await res.json();
      setApplyResult(data);
      
      if (data.ok) {
        setFiles([]);
        setErrorFiles([]);
        setSuccessFiles([]);
        setPdfUrl(null);
      }
      
    } catch (e) {
      setError(`Verschieben fehlgeschlagen: ${e.message}`);
    } finally {
      setApplying(false);
    }
  };

  // === Neuer Batch für bearbeitete Dateien ===
  const handleReprocessEdited = async () => {
    if (editedFileIds.size === 0) return;
    
    setReprocessing(true);
    setError(null);
    
    try {
      // Welle 79: die bearbeiteten Dateien wirklich neu verarbeiten (Reprocess-Endpoint;
      // der Preview-Endpoint ignorierte den Body und lud nur die Liste neu)
      const ids = [...editedFileIds];
      const res = await fetch(`${API_BASE}/batch/dropbox/reprocess?batch_id=${batchId}&file_ids=${encodeURIComponent(ids.join(","))}`, {
        method: "POST",
      });
      
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      
      const data = await res.json();
      console.log("[REPROCESS] Result:", data);
      
      // Aktualisierte Dateien in die Liste mischen
      const byId = Object.fromEntries((data.files || []).map(f => [f.id, f]));
      const merged = files.map(f => byId[f.id] ? { ...f, ...byId[f.id] } : f);
      setFiles(merged);
      
      const errors = merged.filter(f => f.has_error);
      const success = merged.filter(f => !f.has_error);
      
      setErrorFiles(errors);
      setSuccessFiles(success);
      setEditedFileIds(new Set()); // Reset
      
      // Toast anzeigen
      const newSuccess = success.length;
      setToast({
        show: true,
        message: `${newSuccess} Datei(en) erfolgreich verarbeitet`,
        type: newSuccess > 0 ? "success" : "info",
      });
      
      // Erste Datei auswählen
      const firstFile = errors.length > 0 ? errors[0] : success[0];
      if (firstFile) {
        handleFileSelect(firstFile, data.batch_id);
      }
      
    } catch (e) {
      console.error("[REPROCESS] Error:", e);
      setError(`Reprocess fehlgeschlagen: ${e.message}`);
    } finally {
      setReprocessing(false);
    }
  };

  // === Add Entry Modal Handler ===
  const openAddEntryModal = (file) => {
    // Ermittle Error-Tag und empfohlene Tabelle
    const errorTags = file.error_tags || file.warnings || [];
    const firstErrorTag = errorTags.find(t => ERROR_TABLE_MAP[t]) || errorTags[0] || "missing_sender";
    const tableInfo = ERROR_TABLE_MAP[firstErrorTag] || { table: "absender", label: "Absender" };
    
    setAddEntryTarget({
      fileId: file.id,
      errorTag: firstErrorTag,
      defaultTable: tableInfo.table,
      prefillData: {
        sender_candidates: file.sender_candidates || [],
        prefix_candidates: file.prefix_candidates || [],
        date_candidates: file.date_candidates || [],
        doc_schema: file.schema || file.doc_schema || "",
      },
    });
    setAddEntryModalOpen(true);
  };

  const handleEntrySaved = (table, data) => {
    // Markiere Datei als bearbeitet
    if (addEntryTarget?.fileId) {
      setEditedFileIds(prev => new Set([...prev, addEntryTarget.fileId]));
    }
    
    // Toast anzeigen
    const tableLabels = {
      absender: "Absender",
      empfaenger_person: "Empfänger",
      empfaenger_objekt: "Empfänger",
      empfaenger_vertrag: "Empfänger",
      schema_keywords: "Schema-Keyword",
      special_rules: "Sonderregel",
    };
    setToast({
      show: true,
      message: `✅ ${tableLabels[table] || "Eintrag"} "${data.bezeichnung || data.keyword || data.anchor_token}" gespeichert`,
      type: "success",
    });
  };

  // === Render File Row ===
  const FileRow = ({ file, isError }) => {
    const isSelected = selectedFile?.id === file.id;
    const isEdited = editedFileIds.has(file.id);
    const isEditing = editingFileId === file.id;
    
    return (
      <div 
        className={`file-row ${isSelected ? 'selected' : ''} ${isError ? 'error' : 'success'} ${isEdited ? 'edited' : ''}`}
        onClick={() => handleFileSelect(file)}
      >
        <div className="file-icon">
          {isEdited ? (
            <span className="edited-badge" title="Bearbeitet - bereit für neuen Batch">✏️</span>
          ) : isError ? (
            <FaExclamationTriangle />
          ) : (
            <FaCheckCircle />
          )}
        </div>
        <div className="file-info">
          <div className="file-original" title={file.original_name}>
            {file.original_name}
          </div>
          {isEditing ? (
            <div className="file-edit" onClick={e => e.stopPropagation()}>
              <input
                ref={inputRef}
                type="text"
                value={editValue}
                onChange={(e) => handleNameChange(e.target.value)}
                onBlur={() => {
                  if (!editCancelledRef.current) {
                    saveFilename(file.id, editValue);
                  }
                  editCancelledRef.current = false;
                  stopEdit();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    inputRef.current?.blur();
                  } else if (e.key === "Escape") {
                    editCancelledRef.current = true;
                    inputRef.current?.blur();
                  }
                }}
              />
              <span className="ext">.pdf</span>
              {saving && <FaSpinner className="spin" />}
            </div>
          ) : (
            <div 
              className="file-proposed"
              onClick={(e) => startEdit(e, file.id, file.proposed_name)}
              title="Klicken zum Bearbeiten"
            >
              <span>{file.proposed_name}</span>
              <FaEdit className="edit-icon" />
            </div>
          )}
          {/* Welle 79: Alternativer Name der anderen Pipeline → Klick übernimmt */}
          {!isEditing && (file.alternativen || []).length > 0 && (
            <div className="file-alternativen" onClick={(e) => e.stopPropagation()}>
              {file.alternativen.map((a) => (
                <button
                  key={a.quelle}
                  className={`alt-chip alt-${a.quelle}`}
                  disabled={saving}
                  title={`${a.quelle === "gemini" ? "Gemini" : "Pipeline 1"} schlägt vor: ${a.name} — klicken zum Übernehmen`}
                  onClick={() => saveFilename(file.id, a.name, a.quelle === "gemini" ? "gemini_batch" : "v1_batch")}
                >
                  {a.quelle === "gemini" ? "✨ Gemini" : "v1"}: <span className="alt-name">{a.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="file-row-actions">
          {isError && !isEdited && (
            <button 
              className="add-entry-btn"
              onClick={(e) => {
                e.stopPropagation();
                openAddEntryModal(file);
              }}
              title="Eintrag in DB hinzufügen"
            >
              <FaPlus />
            </button>
          )}
          <div className={`route-badge ${file.route?.toLowerCase()}`}>
            {file.route}
          </div>
        </div>
      </div>
    );
  };

  // === Loading State ===
  if (loading) {
    return (
      <div className="review-page loading-page">
        <div className="loading-state">
          <FaSpinner className="spin large" />
          <span>Lade Dateien aus Dropbox...</span>
        </div>
      </div>
    );
  }

  // === Success State ===
  if (applyResult?.ok) {
    return (
      <div className="review-page success-page">
        <div className="success-state">
          <FaCheckCircle className="large" />
          <h2>Fertig!</h2>
          <p>{applyResult.moved_count || 0} Dateien wurden erfolgreich verschoben.</p>
          <button className="btn primary" onClick={() => navigate("/")}>
            <FaArrowLeft /> Zurück zur Startseite
          </button>
        </div>
      </div>
    );
  }

  // === Main Render ===
  return (
    <div className="review-page">
      {/* Header */}
      <header className="review-header">
        <div className="header-left">
          <button className="btn icon-btn" onClick={() => navigate("/")} title="Zurück">
            <FaArrowLeft />
          </button>
          <h1>Batch Review</h1>
          <div className="header-stats">
            <span className="stat error">{errorFiles.length} Fehler</span>
            <span className="stat success">{successFiles.length} OK</span>
            {editedFileIds.size > 0 && (
              <span className="stat edited">{editedFileIds.size} Bearbeitet</span>
            )}
          </div>
        </div>
        <div className="header-right">
          {editedFileIds.size > 0 && (
            <button 
              className="btn reprocess-btn"
              onClick={handleReprocessEdited}
              disabled={reprocessing}
              title="Bearbeitete Dateien nochmal durch den Batch schicken"
            >
              {reprocessing ? <FaSpinner className="spin" /> : <FaSync />}
              {reprocessing ? "Verarbeite..." : `${editedFileIds.size} neu verarbeiten`}
            </button>
          )}
          <button 
            className="btn primary"
            onClick={handleApplyAll}
            disabled={applying || files.length === 0}
          >
            {applying ? <FaSpinner className="spin" /> : null}
            {applying ? "Verschiebe..." : `${files.length - editedFileIds.size} verschieben`}
          </button>
        </div>
      </header>
      <GeminiWarnung wofuer="Der Rename-me-Batch läuft nicht, bis Gemini wieder geht. Neu auswerten nutzt nur v1." />

      {/* Error Banner */}
      {error && (
        <div className="error-banner">
          <FaExclamationTriangle />
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Main Content */}
      <main className="review-main">
        {/* File List - Left Side */}
        <aside className="file-list-panel">
          {/* Fehler-Dateien */}
          {errorFiles.length > 0 && (
            <section className="file-section">
              <h3
                className="section-title error collapsible"
                onClick={() => toggleSection('fehler')}
              >
                <span className={`collapse-arrow ${collapsed.fehler ? 'closed' : ''}`}>&#9660;</span>
                <FaExclamationTriangle /> Fehler ({errorFiles.length})
              </h3>
              {!collapsed.fehler && (
                <div className="section-files">
                  {errorFiles.map(f => <FileRow key={f.id} file={f} isError={true} />)}
                </div>
              )}
            </section>
          )}
          
          {/* Erfolgreiche Dateien */}
          {successFiles.length > 0 && (
            <section className="file-section">
              <h3
                className="section-title success collapsible"
                onClick={() => toggleSection('ok')}
              >
                <span className={`collapse-arrow ${collapsed.ok ? 'closed' : ''}`}>&#9660;</span>
                <FaCheckCircle /> OK ({successFiles.length})
              </h3>
              {!collapsed.ok && (
                <div className="section-files">
                  {successFiles.map(f => <FileRow key={f.id} file={f} isError={false} />)}
                </div>
              )}
            </section>
          )}
          
          {files.length === 0 && (
            <div className="empty-state">
              <p>Keine Dateien gefunden.</p>
              <button className="btn" onClick={loadFiles}>
                <FaRedo /> Neu laden
              </button>
            </div>
          )}
        </aside>

        {/* PDF Viewer - Right Side */}
        <section className="pdf-viewer-panel">
          {/* Info Bar */}
          {selectedFile && (
            <div className="pdf-info-bar">
              <span className="current-file">{selectedFile.original_name}</span>
              <span className={`route-badge ${selectedFile.route?.toLowerCase()}`}>
                {selectedFile.route}
              </span>
            </div>
          )}
          
          {/* PDF Container */}
          <div className="pdf-container">
            {pdfLoading && (
              <div className="pdf-loading">
                <FaSpinner className="spin large" />
                <span>Lade PDF...</span>
              </div>
            )}
            
            {pdfUrl && !pdfLoading && (
              <object
                data={pdfUrl}
                type="application/pdf"
                className="pdf-object"
              >
                <p>PDF kann nicht angezeigt werden. 
                  <a href={pdfUrl} target="_blank" rel="noopener noreferrer">Hier öffnen</a>
                </p>
              </object>
            )}
            
            {!pdfUrl && !pdfLoading && selectedFile && (
              <div className="pdf-error">
                <FaExclamationTriangle />
                <span>PDF konnte nicht geladen werden</span>
              </div>
            )}
            
            {!selectedFile && (
              <div className="pdf-placeholder">
                <span>Wähle eine Datei aus der Liste</span>
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Footer Hint */}
      <footer className="review-footer">
        <span>💡 Text kann markiert und kopiert werden · Zoom mit Trackpad oder Strg+Mausrad</span>
      </footer>

      {/* Add Entry Modal */}
      <AddEntryModal
        isOpen={addEntryModalOpen}
        onClose={() => setAddEntryModalOpen(false)}
        onSaved={handleEntrySaved}
        defaultTable={addEntryTarget?.defaultTable}
        prefillData={addEntryTarget?.prefillData || {}}
        errorTag={addEntryTarget?.errorTag}
      />

      {/* Toast Notification */}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
        duration={3000}
      />
    </div>
  );
};

export default RenameBatchPage;
